import { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { TravelPersonForm } from '../components/TravelPersonForm';
import { KidMenuCard } from '../components/KidMenuCard';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { CheckCircle2, Loader2, Plane, Trash2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRAVEL_FIELDS = [
  'travelMode', 'arrivalAirport', 'arrivalAirportOther', 'departureAirport', 'departureAirportOther',
  'arrivalDate', 'arrivalTime', 'arrivalFlight',
  'departureDate', 'departureTime', 'departureFlight',
  'transferArrival', 'transferArrivalOther', 'transferDeparture', 'transferDepartureOther', 'hotel'
];

const emptyPerson = () => ({
  uid: crypto.randomUUID(),
  guestName: '',
  phone: '',
  travelMode: 'flight',
  arrivalAirport: '',
  arrivalAirportOther: '',
  departureAirport: '',
  departureAirportOther: '',
  arrivalDate: '',
  arrivalTime: '',
  arrivalFlight: '',
  departureDate: '',
  departureTime: '',
  departureFlight: '',
  transferArrival: '',
  transferArrivalOther: '',
  transferDeparture: '',
  transferDepartureOther: '',
  hotel: '',
  coldAppetizer: '',
  mainCourse: '',
  allergies: [],
  allergiesOther: '',
  drinks: [],
  travelingWithChild: '',
  beautyService: '',
  comments: '',
  sameTravelAsFirst: false
});

const emptyKid = () => ({
  uid: crypto.randomUUID(),
  guestName: '',
  kidsAppetizer: '',
  kidsMain: ''
});

export default function TravelForm() {
  const { t } = useLanguage();
  const [guests, setGuests] = useState([]);
  const [persons, setPersons] = useState([emptyPerson()]);
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [submittedAlert, setSubmittedAlert] = useState(null);

  useEffect(() => {
    const fetchGuests = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/travel-form/guests`);
        setGuests(res.data);
      } catch {
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };
    fetchGuests();
  }, []);

  useEffect(() => {
    if (error) setError(null);
  }, [persons, kids, error]);

  const adultGuests = guests.filter(g => !g.isKid);
  const kidGuests = guests.filter(g => g.isKid);

  const availableFor = (index) => {
    const selectedElsewhere = persons
      .filter((_, i) => i !== index)
      .map(p => p.guestName)
      .filter(Boolean);
    return adultGuests.filter(g => !selectedElsewhere.includes(g.name));
  };

  const availableKidsFor = (index) => {
    const selectedElsewhere = kids
      .filter((_, i) => i !== index)
      .map(k => k.guestName)
      .filter(Boolean);
    return kidGuests.filter(g => !selectedElsewhere.includes(g.name));
  };

  const handleNameSelect = (index, name) => {
    const guest = adultGuests.find(g => g.name === name);
    if (guest?.hasSubmitted) {
      setSubmittedAlert(name);
      return;
    }
    const p = guest?.prefill || {};
    // Guest has their own travel data in the master list if any flight-specific field is prefilled
    const hasOwnTravelData = !!(p.arrivalDate || p.arrivalFlight || p.arrivalAirport);
    setPersons(prev => prev.map((person, i) => {
      if (i !== index) return person;
      // If switching to a different guest, replace prefill fields with new guest's data (even if empty).
      // If first-time selection (no prior name), fill from master data.
      const isChangingGuest = person.guestName && person.guestName !== name;
      const fromPrefill = (key) => (isChangingGuest ? (p[key] || '') : (p[key] || person[key]));
      return {
        ...person,
        guestName: name,
        phone: fromPrefill('phone'),
        arrivalAirport: fromPrefill('arrivalAirport'),
        departureAirport: fromPrefill('departureAirport'),
        arrivalDate: fromPrefill('arrivalDate'),
        arrivalTime: fromPrefill('arrivalTime'),
        arrivalFlight: fromPrefill('arrivalFlight'),
        departureDate: fromPrefill('departureDate'),
        departureTime: fromPrefill('departureTime'),
        departureFlight: fromPrefill('departureFlight'),
        hotel: fromPrefill('hotel'),
        // Default to "same as guest 1" unless this guest has their own travel data
        sameTravelAsFirst: i > 0 && !hasOwnTravelData
      };
    }));
  };

  const handlePersonChange = (index, updated) => {
    setPersons(prev => prev.map((p, i) => (i === index ? updated : p)));
  };

  const handleKidChange = (index, updated) => {
    setKids(prev => prev.map((k, i) => (i === index ? updated : k)));
  };

  const handleKidNameSelect = (index, kid, name) => {
    const guest = kidGuests.find(g => g.name === name);
    if (guest?.hasSubmitted) {
      setSubmittedAlert(name);
      return;
    }
    handleKidChange(index, { ...kid, guestName: name });
  };

  const validate = () => {
    for (let i = 0; i < persons.length; i++) {
      const p = persons[i];
      if (!p.guestName) return t('travelForm.errorSelectName');
      const required = [p.phone, p.coldAppetizer, p.mainCourse, p.beautyService];
      if (i === 0 || !p.sameTravelAsFirst) {
        if (p.travelMode === 'other') {
          required.push(p.arrivalDate, p.hotel);
        } else {
          required.push(
            p.arrivalAirport, p.departureAirport,
            p.arrivalDate, p.arrivalTime, p.arrivalFlight,
            p.departureDate, p.departureTime, p.departureFlight,
            p.transferArrival, p.transferDeparture, p.hotel
          );
          if ((p.arrivalAirport === 'other' && !p.arrivalAirportOther) ||
              (p.departureAirport === 'other' && !p.departureAirportOther)) {
            return `${t('travelForm.errorRequired')} ${p.guestName}`;
          }
        }
      }
      if (required.some(v => !v)) return `${t('travelForm.errorRequired')} ${p.guestName}`;
    }
    for (const k of kids) {
      if (!k.guestName.trim()) return t('travelForm.errorChildName');
      if (!k.kidsAppetizer || !k.kidsMain) return `${t('travelForm.errorRequired')} ${k.guestName}`;
    }
    return null;
  };

  const buildPayload = () => {
    const first = persons[0];
    const adults = persons.map(({ uid, sameTravelAsFirst, ...person }, i) => {
      if (i > 0 && sameTravelAsFirst) {
        TRAVEL_FIELDS.forEach(f => { person[f] = first[f]; });
      }
      return person;
    });
    const children = kids.map(k => ({
      guestName: k.guestName.trim(),
      isChild: true,
      kidsAppetizer: k.kidsAppetizer,
      kidsMain: k.kidsMain
    }));
    return { persons: [...adults, ...children] };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/travel-form`, buildPayload());
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen cream-bg">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center px-4 pt-20">
          <Card className="max-w-md w-full border-2 border-primary shadow-xl cream-bg" data-testid="travel-form-success">
            <CardContent className="pt-12 pb-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 blue-text" />
              </div>
              <h2 className="font-serif text-3xl font-semibold blue-text mb-4">{t('travelForm.thankYou')}</h2>
              <p className="blue-text opacity-80">{t('travelForm.successMessage')}</p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen cream-bg">
      <Navbar />
      <section className="pt-32 pb-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Plane className="w-10 h-10 blue-text mx-auto mb-6" />
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold blue-text mb-6" data-testid="travel-form-title">
            {t('travelForm.title')}
          </h1>
          <p className="font-serif text-lg blue-text mb-2">{t('travelForm.intro1')}</p>
          <p className="font-sans blue-text opacity-80 mb-2">{t('travelForm.intro2')}</p>
          <p className="font-sans blue-text opacity-80">{t('travelForm.intro3')} 🤍</p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 blue-text animate-spin" />
            </div>
          ) : loadFailed ? (
            <p className="text-red-600 text-center font-semibold" data-testid="travel-form-load-error">
              {t('travelForm.loadError')}
            </p>
          ) : adultGuests.length === 0 ? (
            <Card className="border-2 border-primary cream-bg">
              <CardContent className="pt-8 pb-8 text-center">
                <p className="blue-text" data-testid="all-submitted-message">{t('travelForm.allSubmitted')}</p>
              </CardContent>
            </Card>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="travel-form">
              {/* Names selection */}
              <Card className="border-2 border-primary cream-bg" data-testid="names-section">
                <CardHeader className="pb-2">
                  <CardTitle className="font-serif text-2xl blue-text">{t('travelForm.whoSection')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {persons.map((person, index) => (
                    <div key={person.uid}>
                      <Label className="blue-text">{t('travelForm.person')} {index + 1} *</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={person.guestName} onValueChange={(name) => handleNameSelect(index, name)}>
                          <SelectTrigger className="border-2 border-primary cream-bg blue-text flex-1" data-testid={`guest-name-select-${index}`}>
                            <SelectValue placeholder={t('travelForm.selectNamePlaceholder')} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {availableFor(index).map((g) => (
                              <SelectItem key={g.name} value={g.name} data-testid={`guest-option-${g.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                {g.name}{g.hasSubmitted ? ` — ${t('travelForm.alreadySubmittedTag')}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setPersons(prev => prev.filter((_, i) => i !== index))}
                            className="border-2 border-red-400 text-red-600 hover:bg-red-50 shrink-0"
                            data-testid={`remove-person-btn-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}

                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPersons(prev => [...prev, emptyPerson()])}
                      className="border-2 border-primary blue-text hover:bg-blue-50"
                      data-testid="add-person-btn"
                    >
                      {t('travelForm.addPerson')}
                    </Button>
                    <p className="text-xs blue-text opacity-70 mt-1">{t('travelForm.addPersonNote')}</p>
                  </div>

                  {kids.map((kid, index) => (
                    <div key={kid.uid}>
                      <Label className="blue-text">{t('travelForm.childLabel')} {index + 1} *</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={kid.guestName} onValueChange={(name) => handleKidNameSelect(index, kid, name)}>
                          <SelectTrigger className="border-2 border-primary cream-bg blue-text flex-1" data-testid={`kid-name-select-${index}`}>
                            <SelectValue placeholder={t('travelForm.selectNamePlaceholder')} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {availableKidsFor(index).map((g) => (
                              <SelectItem key={g.name} value={g.name} data-testid={`kid-option-${g.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                {g.name}{g.hasSubmitted ? ` — ${t('travelForm.alreadySubmittedTag')}` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setKids(prev => prev.filter((_, i) => i !== index))}
                          className="border-2 border-red-400 text-red-600 hover:bg-red-50 shrink-0"
                          data-testid={`remove-kid-btn-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {kids.length < kidGuests.length && (
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setKids(prev => [...prev, emptyKid()])}
                        className="border-2 border-primary blue-text hover:bg-blue-50"
                        data-testid="add-child-btn"
                      >
                        {t('travelForm.addChild')}
                      </Button>
                      <p className="text-xs blue-text opacity-70 mt-1">{t('travelForm.addChildNote')}</p>
                    </div>
                  )}

                  <p className="text-xs blue-text opacity-70">{t('travelForm.selectNameNote')}</p>
                </CardContent>
              </Card>

              {/* Per-person details — only show once a name has been selected */}
              {persons.map((person, index) => (
                person.guestName ? (
                  <TravelPersonForm
                    key={person.uid}
                    index={index}
                    person={person}
                    firstPersonName={persons[0].guestName}
                    onChange={handlePersonChange}
                  />
                ) : null
              ))}

              {/* Kids menus — only show once a name has been selected */}
              {kids.map((kid, index) => (
                kid.guestName ? (
                  <KidMenuCard key={kid.uid} index={index} kid={kid} onChange={handleKidChange} />
                ) : null
              ))}

              {error && (
                <p className="text-red-600 text-center font-semibold" data-testid="travel-form-error">{error}</p>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full blue-bg text-white hover:opacity-90 py-6 text-lg tracking-widest"
                data-testid="travel-form-submit-btn"
              >
                {submitting ? t('travelForm.submitting') : t('travelForm.submit')}
              </Button>
            </form>
          )}
        </div>
      </section>
      <Footer />

      <AlertDialog open={!!submittedAlert} onOpenChange={(open) => !open && setSubmittedAlert(null)}>
        <AlertDialogContent className="cream-bg border-2 border-primary" data-testid="already-submitted-dialog">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-2">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <AlertDialogTitle className="font-serif text-2xl blue-text">
              {t('travelForm.alreadySubmittedTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="blue-text opacity-80 space-y-2">
              <span className="block">
                {t('travelForm.alreadySubmittedIntro')} <strong>{submittedAlert}</strong>.
              </span>
              <span className="block">{t('travelForm.alreadySubmittedContact')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              className="blue-bg text-white hover:opacity-90"
              onClick={() => setSubmittedAlert(null)}
              data-testid="already-submitted-dialog-ok"
            >
              {t('travelForm.alreadySubmittedOk')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
